import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Button, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { type FC, type ReactNode } from "react";
import {
  HANDLING_COPY,
  formatHandlingTimeShort,
  handlingIconColor,
  handlingPillSx,
  handlingReleaseButtonSx,
  handlingSinceTooltip,
  type HandlingTone,
} from "./handling-ui";

interface OpportunityHandlingStatusPillProps {
  tone: HandlingTone;
  managerName?: string | null;
  handlingSince?: Date | string | null;
  size?: "sm" | "md";
  showRelease?: boolean;
  releasePending?: boolean;
  onRelease?: () => void;
  releaseTooltip?: string;
  trailing?: ReactNode;
}

export const OpportunityHandlingStatusPill: FC<
  OpportunityHandlingStatusPillProps
> = ({
  tone,
  managerName,
  handlingSince,
  size = "md",
  showRelease = false,
  releasePending = false,
  onRelease,
  releaseTooltip,
  trailing,
}) => {
  const sinceShort = formatHandlingTimeShort(handlingSince);
  const sinceTip = handlingSinceTooltip(handlingSince);

  const statusLabel =
    tone === "mine"
      ? HANDLING_COPY.mineStatus
      : tone === "other"
        ? HANDLING_COPY.otherStatus(managerName ?? "מנהל אחר")
        : "";

  const label = (
    <Tooltip title={sinceTip ?? ""} disableHoverListener={!sinceTip}>
      <Typography
        variant="body2"
        component="span"
        color={tone === "mine" ? "text.primary" : "text.secondary"}
        fontWeight={500}
        fontSize={size === "sm" ? "0.8125rem" : "0.875rem"}
        sx={{ lineHeight: 1.3 }}
      >
        {statusLabel}
        {sinceShort ? (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            fontSize="inherit"
            sx={{ opacity: 0.85 }}
          >
            {` · ${HANDLING_COPY.since(sinceShort)}`}
          </Typography>
        ) : null}
      </Typography>
    </Tooltip>
  );

  return (
    <Stack sx={handlingPillSx(tone, size)} flexWrap="nowrap">
      <PersonOutlineIcon
        sx={{
          fontSize: size === "sm" ? "1rem" : "1.125rem",
          color: handlingIconColor(tone),
          flexShrink: 0,
        }}
      />
      {label}
      {showRelease && onRelease && (
        <Tooltip title={releaseTooltip ?? HANDLING_COPY.releaseAction} arrow>
          <Button
          size="small"
          variant="text"
          color="inherit"
          disabled={releasePending}
          onClick={(e) => {
            e.stopPropagation();
            onRelease();
          }}
          sx={handlingReleaseButtonSx}
        >
          {releasePending ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            HANDLING_COPY.releaseAction
          )}
        </Button>
        </Tooltip>
      )}
      {trailing}
    </Stack>
  );
};
